// @flow
import React, { useContext, useEffect } from 'react';
import { ceil, chunk } from 'lodash';
import { Collapse } from 'react-collapse';
import classnames from 'classnames';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { PriceRemoteContext } from 'Components/widgets/price-remote/PriceRemoteContext';
import { PriceRemoteServiceList } from 'Components/widgets/price-remote/price-remote-service-list/PriceRemoteServiceList';
import { type Service } from 'Components/widgets/product-services/product-service/ProductService';
import { t } from 'Src/i18n';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

export type SeasonalService = {
  sun: Service,
  snow: Service,
};

export type ServicesConfig = {
  room: SeasonalService,
  activities: SeasonalService,
  bar: SeasonalService,
  restaurant: SeasonalService,
  transport: SeasonalService,
  transfer: SeasonalService,
  childcare: SeasonalService,
  childcarebaby: SeasonalService,
  ski: SeasonalService,
};

export type PriceRemoteDetailsProps = {
  allInclusiveServices: Array<Service>,
  isLoading?: boolean,
  isSticky: boolean,
};

export const PriceRemoteDetails = ({
  isSticky,
  allInclusiveServices,
  isLoading,
}: PriceRemoteDetailsProps) => {
  const { isDesktop } = useContext(ResponsiveContext);

  const [priceRemoteContext, setPriceRemoteContext] = useContext(PriceRemoteContext);

  useEffect(
    () => {
      if (allInclusiveServices && allInclusiveServices.length > 0 && isDesktop) {
        setPriceRemoteContext({ isDetailOpened: true });
      }
    },
    [allInclusiveServices.map(({ label }) => label).join('|'), isSticky],
  );

  const trackingData = {
    event: 'eventGA',
    categoryGA: 'resort',
    actionGA: 'priceBar',
    labelGA: 'checkPrices',
    valueGA: '0',
    nonInteraction: '0',
  };

  const linkToCalendar = t('priceRemote.linkToCalendar');

  const splitServices =
    allInclusiveServices && allInclusiveServices.length > 0
      ? chunk([...allInclusiveServices], ceil(allInclusiveServices.length / 2))
      : [];

  return (
    <Collapse isOpened={priceRemoteContext.isDetailOpened} className="px-5">
      <div
        className={classnames('absolute', {
          'rtl:right-0 mt-1': isDesktop,
          'flex w-full mt-10 justify-center': !isDesktop,
        })}
      >
        {!(priceRemoteContext.isDetailOpened || isSticky) &&
          linkToCalendar && (
            <TrackClick
              className="bg-transparent text-center text-blue underline text-base font-bold"
              trackingData={trackingData}
              as={DynamicLink}
              url="#booking"
            >
              {linkToCalendar}
            </TrackClick>
          )}
      </div>
      {priceRemoteContext.isTotalPrice && (
        <div className="text-base text-blue font-bold text-center pt-4 pb-1 lg:pt-2">
          {t('priceRemote.priceForAll')}
        </div>
      )}
      <div
        className={classnames('flex flex-wrap pb-4 lg:pb-8 transition-opacity', {
          'opacity-25': isLoading,
        })}
        data-testid="price-remote-details-container"
      >
        {splitServices[0] ? (
          <PriceRemoteServiceList services={splitServices[0]} isDesktop={isDesktop} />
        ) : null}
        {splitServices[1] ? (
          <PriceRemoteServiceList services={splitServices[1]} isDesktop={isDesktop} />
        ) : null}
      </div>
    </Collapse>
  );
};
